<i18n>
{
  "fr": {
    "search-in": "Rechercher dans",
    "search": "Rechercher dans la carte active...",
    "no-results": "Aucun résultat.",
    "in-layer-results": "Dans:",
    "loading": "Chargement...",
    "communes": "Communes"
  },
  "de": {

  }
}
</i18n>

<template>
  <div class="search-on-map" v-if="show">
    <div class="search-input">
      <md-field>
        <div @click="search">
          <md-icon>search</md-icon>
          <md-tooltip>{{ $t('search') }}</md-tooltip>
        </div>
        <md-input v-model="terms" :placeholder="placeholder" @keyup="searchDone = false" @keyup.enter="search" @focus="showSearchResult = true"></md-input>
        <div @click="terms = ''; searchDone = false">
          <md-icon>clear</md-icon>
        </div>
      </md-field>
      <md-button class="md-button md-theme-default md-icon-button md-primary" @click="showSearchHelpPopup = true">
        <md-icon>info</md-icon>
        <md-tooltip md-direction="bottom">{{ $t('info-label') }}</md-tooltip>
      </md-button>
    </div>

    <md-content class="results md-scrollbar" v-if="!loading && showSearchResult && searchDone">
      <div v-for="result in foundInLayers" :key="result.layer.id">
        <h3 v-if="layers.length > 1">{{ $t('in-layer-results') }} {{ result.layer.metadata.name }}</h3>
        <div class="result-items">
          <div class="result-item" v-for="item in result.found" :key="item.properties.oid" @click="inLayerSelected(item, result.layer)">
            <GeomTypeIcon class="geom-type-icon" :geomType="result.layer.geomType" />
            {{ `${item.properties.name} ${item.properties?.nom ? '- ' + item.properties.nom : ''}` }}
          </div>
        </div>
      </div>

      <div v-if="coordinate">
        <div class="result-item" @click="coordinateSelected">Zoomer sur {{ printEpsg2056(coordinate[0], coordinate[1], 1) }}</div>
      </div>

      <template v-if="communes.length">
        <h3>{{ $t('communes') }}</h3>
        <div class="result-items">
          <div v-for="commune in communes" :key="commune.name" class="result-item" @click="communeSelected(commune)">{{ commune.name }}</div>
        </div>
      </template>

      <template v-if="parcels.length">
        <h3>{{ $t('parcels') }}</h3>
        <div class="result-items">
          <div v-for="parcel in parcels" :key="parcel.name" class="result-item" @click="parcelSelected(parcel)">{{ parcel.name }}</div>
        </div>
      </template>

      <div v-if="foundInLayers.length === 0 && coordinate === null && communes.length === 0 && parcels.length === 0">{{ $t('no-results') }}</div>
    </md-content>

    <div v-if="loading">{{ $t('loading') }}</div>

    <SearchHelp :show="showSearchHelpPopup" @close="showSearchHelpPopup = false" />
  </div>
</template>

<script>
import _ from 'lodash'
import MapMixin from '@/mixins/store/map'
import InteractionMixin from '@/mixins/store/interaction'
import LayerMixin from '@/mixins/store/layer'
import GeomTypeIcon from '@/components/vue-map/common/geomTypeIcon'
import { printEpsg2056 } from '@/utils/coordinate.js'
import axios from 'axios'
import { sortBy } from '@/utils/array.js'
import camelize from 'camelize'
import GeoJSON from 'ol/format/GeoJSON.js';
import SearchHelp from '@/components/common/search-help.vue'

export default {
  name: 'Search',
  components: {
    GeomTypeIcon,
    SearchHelp
},
  props: {
    layers: {
      type: Array,
      default: () => { return [] }
    }
  },
  mixins: [
    MapMixin,
    InteractionMixin,
    LayerMixin
  ],
  data () {
    return {
      terms: '',
      searchDone: false,
      showSearchResult: false,
      loading: false,
      foundInLayers: [],
      coordinate: null,
      communes: [],
      parcels: [],
      printEpsg2056,
      showSearchHelpPopup: false
    }
  },
  computed: {
    show () {
      return this.layers.length > 0
    },
    placeholder () {
      if (this.layers.length > 1) return this.$t('search')
      let layer = _.head(this.layers)
      return `${this.$t('search-in')} "${layer.metadata.name}"`
    }
  },
  methods: {
    inRange (number, start, stop) {
      if (start > stop) {
        [start, stop] = [stop, start]
      }
      return start <= number && number <= stop
    },
    checkInRangeAndOrdere (e, n) {
      e = parseFloat(e)
      n = parseFloat(n)

      if (isNaN(e) || isNaN(n)) return null

      const E_MIN = 2420000
      const E_MAX = 2900000
      const N_MIN = 1030000
      const N_MAX = 1350000

      if (this.inRange(e, E_MIN, E_MAX) && this.inRange(n, N_MIN, N_MAX)) {
        return [e, n]
      }

      if (this.inRange(e, N_MIN, N_MAX) && this.inRange(n, E_MIN, E_MAX)) {
        return [n, e]
      }

      return null
    },
    async search () {
      if (this.terms === '') return

      let terms = _.trim(this.terms)

      this.searchDone = false
      this.showSearchResult = true
      this.loading = true
      this.foundInLayers = []
      this.coordinate = null
      this.communes = []

      let coordinate = _.clone(terms)
      coordinate = coordinate.replaceAll("'", '')
      coordinate = coordinate.match(/^((?:\d|[.])+?)\W+((?:\d|[.])+)$/)

      if (coordinate) {
        coordinate = this.checkInRangeAndOrdere(coordinate[1], coordinate[2])
      }

      if (coordinate) {
        this.coordinate = coordinate
      } else {
        let results = []
        for (let layer of this.layers) {
          try {
            let [featureCollection, featuresByPublicId] = await Promise.all([
              layer.searchFeatures(this.terms),
              layer.searchFeatures(this.terms, 'public_id')
            ])
            featureCollection.features = _.uniqBy(featureCollection.features.concat(featuresByPublicId.features), 'id')
            results.push({
              layer,
              featureCollection
            })
          } catch {
            // Pas de problème si pas de données...
          }
        }

        results.forEach(({ layer, featureCollection }) => {
          if (featureCollection.features.length) {
            this.foundInLayers.push({
              layer,
              found: _.orderBy(featureCollection.features, ['properties.name'], ['asc'])
            })
          }
        })

        // Communes
        let response = await axios.get(`https://api3.geo.admin.ch/rest/services/api/MapServer/find?layer=ch.swisstopo.swissboundaries3d-gemeinde-flaeche.fill&searchField=gemname&searchText=${encodeURIComponent(this.terms)}`)
        if (response.status === 200) {
          let communes = response.data.results.map(commune => ({
            name: commune.attributes.gemname,
            bbox: commune.bbox
          }))

          this.communes = sortBy(communes, 'name')
        }

        // Numéro parcellaire
        let result = terms.match(/(?=.*?(\d+))(?=.*?(\D+))/)
        if (result) {
          let parcelNumber = result[1]
          let communeName = _.trim(result[2])

          let value = parseInt(this.terms, 10)
          if (!isNaN(value) && isFinite(value)) {
            let response = await axios.get(`https://sit.vs.ch/arcgis/rest/services/MO/MapServer/80/query?where=NUMERO%3D%27${parcelNumber}%27+AND+COMMUNE+LIKE+%27%25${encodeURIComponent(communeName)}%25%27&outFields=*&outSR=2056&f=geojson`)
            if (response.status === 200) {

              let parcels = response.data.features.map(parcel => ({
                name: `${parcel.properties.NUMERO} ${parcel.properties.COMMUNE}`,
                bbox: new GeoJSON().readFeature(parcel).getGeometry().getExtent()
              }))

              this.parcels = sortBy(parcels, 'name')
            }
          }
        }
      }

      this.searchDone = true
      this.loading = false
    },
    inLayerSelected (item, layer) {
      this.setCurrentGeom(item.geometry)
      this.setCurrentObject(camelize(item.properties))
      this.centerTo(item.geometry.coordinates, layer.webviewConfig.zoomToFeature)
      this.selectLayer(layer)
      this.showSearchResult = false
    },
    coordinateSelected () {
      this.centerTo(this.coordinate, 30)
    },
    communeSelected (commune) {
      this.zoomToExtent(commune.bbox)
    },
    parcelSelected (parcel) {
      this.zoomToExtent(parcel.bbox, false)
    }
  }
}
</script>

<style lang="scss" scoped>
  p {
    margin: 0
  }

  .search-on-map {
    background-color: #fefefe;
    padding-left: 30px;
    padding-bottom: 15px;
    padding-top: 0px;
    padding-right: 30px;

    .search-input {
      display: flex;
      align-items: baseline;
    }

    .md-field {
      margin: 0px;
      padding: 0px;
      margin-top: -5px;
      min-height: 32px;
      height: 32px;

      .md-icon {
        margin-top: 4px;
        cursor: pointer;
      }
    }

    .results {
      overflow-y: auto;
      border: 1px solid #e1e1e1;
      margin-top: 0px;
      padding: 10px;
      max-height: 220px;

      h3 {
        font-size: 14px;
        margin-bottom: 0px;
        margin-top: 0px;
        color: #757575;
        text-decoration: underline;
      }

      .result-items {
        padding: 5px;
        padding-left: 15px;

      }

      .result-item {
        cursor: pointer;
      }
    }
  }
</style>
